import {
  WiFiConfigurationRequestBody,
  WiFiConfigurationRequestBodyConfig,
} from '../../../services/types';
import WifiConfigurations from '../../../services/wifi-configurations-v1';
import { LoginState } from '../../../store/login/types';
import { Result } from '../../../store/search/types';

const fillInterfaceMode = (
  wifiConfiguration: any,
  selectedEntity: any,
  extraRowFlag: boolean,
  wifiConfigPayload?: WiFiConfigurationRequestBodyConfig[],
): WiFiConfigurationRequestBodyConfig[] => {
  const dataSet = extraRowFlag
    ? {
        ssid: wifiConfiguration.ssid2,
        security: wifiConfiguration.security2,
        password: wifiConfiguration.password2,
        metric: wifiConfiguration.metric2,
        ipAddress: wifiConfiguration.ipAddress2,
        networkAddress: wifiConfiguration.networkAddress2,
        networkMask: wifiConfiguration.networkMask2,
        dhcpDnsConfig: wifiConfiguration.dhcpDnsConfig2,
      }
    : {
        ssid: wifiConfiguration.ssid,
        security: wifiConfiguration.security,
        password: wifiConfiguration.password,
        metric: wifiConfiguration.metric,
        ipAddress: wifiConfiguration.ipAddress,
        networkAddress: wifiConfiguration.networkAddress,
        networkMask: wifiConfiguration.networkMask,
        dhcpDnsConfig: wifiConfiguration.dhcpDnsConfig,
      };
  const newConfig: WiFiConfigurationRequestBodyConfig = {
    make: selectedEntity.assetMake,
    serialNumber: selectedEntity.assetSerialNumber,
    deviceCharacterization: 'master',
    configType: 'userWiFi',
    transactionId: wifiConfiguration.transactionId,
    wifiConfig: {
      countryCode: wifiConfiguration.countryCode,
      channel: Number.parseInt(wifiConfiguration.channel, 10),
      dnsConfig: {
        primary: 'PRIMARY',
        secondary: 'SECONDARY',
      },
    },
  };
  if (wifiConfiguration.interfaceConfigType === 'ACCESS_POINT') {
    newConfig.wifiConfig.accessPointNetworkEntries = [
      {
        ssid: dataSet.ssid,
        security: dataSet.security,
        password: dataSet.password,
        metric: Number.parseInt(dataSet.metric, 10),
        staticAddressMode: {
          ipAddress: dataSet.ipAddress,
          netMask: dataSet.networkAddress,
        },
      },
    ];
    if (wifiConfiguration.addressMode === 'STATIC') {
      newConfig.wifiConfig.accessPointNetworkEntries[0].staticAddressMode = {
        ipAddress: dataSet.ipAddress,
        netMask: dataSet.networkMask,
      };
    } else {
      newConfig.wifiConfig.accessPointNetworkEntries[0].addressMode = {
        dhcpServerAddressMode: {
          ipAddress: dataSet.ipAddress,
          netMask: dataSet.networkMask,
          provideDefaultGateway: false,
        },
      };
    }
  } else {
    newConfig.wifiConfig.clientNetworkEntries = [
      {
        ssid: dataSet.ssid,
        security: dataSet.security,
        password: dataSet.password,
        metric: Number.parseInt(dataSet.metric, 10),
        dhcpDnsConfig: dataSet.dhcpDnsConfig,
        addressMode: 'DHCP_CLIENT',
      },
    ];
  }
  const payload: WiFiConfigurationRequestBodyConfig[] = wifiConfigPayload
    ? [...wifiConfigPayload, newConfig]
    : [newConfig];

  if (extraRowFlag) return fillInterfaceMode(wifiConfiguration, selectedEntity, false, payload);
  return payload;
};

export const queueWifiConfiguration = async ({
  login,
  selectedEntity,
  wifiConfiguration,
  extraRowFlag,
  snackbarCallback,
}: {
  login: LoginState;
  selectedEntity: Result;
  wifiConfiguration: any;
  extraRowFlag: boolean;
  snackbarCallback: Function;
}) => {
  const {
    client,
    envVariables: { wifiConfigurationsV1 },
  } = login;
  const clientAuth: any = client?.authenticate();
  const userId = clientAuth.loginId;
  const wifiCongfig = new WifiConfigurations(client, wifiConfigurationsV1);
  const configs = fillInterfaceMode(wifiConfiguration, selectedEntity, extraRowFlag);
  const wifiConfigPayload: WiFiConfigurationRequestBody = {
    configs,
    wakeup: false,
    wakeupType: 'all',
    reboot: false,
    userId,
    applicationId: 'iot-gateway',
  };

  try {
    const response = await wifiCongfig.queueConfigs(wifiConfigPayload);
    snackbarCallback({ message: 'Configuration Sent', variant: 'success' });
    return response.data.configs;
  } catch {
    snackbarCallback({
      message: 'There Was a Problem with your submission, please try again',
      variant: 'error',
    });
  }
  return null;
};

export const deleteWifiConfiguration = async ({
  login,
  selectedEntity,
  wifiConfiguration,
  snackbarCallback,
}: {
  login: LoginState;
  selectedEntity: Result;
  wifiConfiguration: any;
  snackbarCallback: Function;
}) => {
  const {
    client,
    envVariables: { wifiConfigurationsV1 },
  } = login;
  const clientAuth: any = client?.authenticate();
  const userId = clientAuth.loginId;
  const wifiCongfig = new WifiConfigurations(client, wifiConfigurationsV1);
  const { transactionId } = wifiConfiguration;
  const deleteWifiPayload = {
    configs: [
      {
        deviceId: selectedEntity.deviceId,
        deviceType: selectedEntity.deviceType,
        configType: 'userWiFi',
        transactionId,
      },
    ],

    wakeup: false,
    wakeupType: 'all',
    reboot: false,
    userId,
    applicationId: 'iot-gateway',
  };
  if (transactionId !== undefined && transactionId.length === 0) {
    delete deleteWifiPayload.configs[0].transactionId;
  }

  try {
    const response = await wifiCongfig.removeConfigs(deleteWifiPayload);
    snackbarCallback({ message: `Successfully Deleted`, variant: 'success' });
    return response.data.configs;
  } catch {
    snackbarCallback({ message: 'There Was a Problem', variant: 'error' });
    return null;
  }
};
